import type { ReactElement } from "react";

import type { Control } from "src/api";
import { Price } from "../unitRows";
import { HoverCard } from "../kit";
import { CardBody } from "./styles";

/**
 * A control's words, on the kit's own surface.
 *
 * **Native `title` is out** (designer ruling, U6 review): a browser tooltip is
 * system chrome in the system font on the system's own clock, and half the
 * controls in here are `aria-disabled`, where the reason is the whole content.
 * `HoverCard` clones its child to attach the ref and the pointer handlers, so
 * this takes exactly one element and hands it straight through.
 *
 * **Where it goes is one answer for every control here**, so it is written
 * once and there is no prop for it: `right-start` off `beside` — the inspector
 * card itself — which puts the card just outside the panel's right edge, level
 * with the trigger. A card must never land on a sibling control, and in a
 * 240px column of stacked rows every direction from the trigger is a sibling
 * except the one that leaves the panel altogether.
 *
 * An item with no card renders its trigger bare rather than an empty panel.
 */
export function Explained({
  item,
  beside,
  children
}: {
  item: Control;
  beside: HTMLElement | null;
  children: ReactElement;
}) {
  if (!item.card) return children;
  return (
    <HoverCard
      title={item.card.title}
      placement="right-start"
      beside={beside}
      data-testid={`card-${item.id}`}
      card={
        <>
          <CardBody>{item.card.body}</CardBody>
          {item.card.rows && <Price rows={item.card.rows} explained />}
        </>
      }
    >
      {children}
    </HoverCard>
  );
}
